// Building programme (V0.4.5): every programme revision is a building file with its dates (read by Claude or typed).
// The section shows contract PC vs EOTs granted vs the latest programme PC, what moved since the last revision, and
// the revisions with their tags and notes. The editor is used both to check Claude's read and to type dates by hand.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, write } from '../../lib/api';
import { uploadFile } from '../../lib/files';
import { supabase } from '../../lib/supabase';
import { useLive } from '../../lib/live';
import { fmtDate } from '../../lib/format';
import { byNewest, cleanProgramme, compare, days, eotCheck, signed, type Milestone, type ProgFile, type Programme } from '../../lib/programme';
import type { Taggable } from '../../lib/fileTags';
import type { Building } from '../../lib/buildings';
import { Button, Drawer, errText, useToast } from '../kit';
import { FileNote, FileStatus, TagEditor } from '../FileTags';
import { useViewer } from '../Viewer';
import { fileTag } from '../../lib/fileType';

export type PFile = ProgFile & Taggable & { mime: string | null; kind: string };
type Eot = { id: string; number: string | null; eot_days_granted: number | null; status: string };

const CLAUDE_READS = /^(application\/pdf|image\/(jpeg|png|webp|gif)|application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet|text\/csv)$/i;
const readable = (f: File) => CLAUDE_READS.test(f.type) || /\.(xlsx|csv)$/i.test(f.name);

/** Programme files + EOT notices for a building, live. */
export function useProgrammes(buildingId: string, version = 0) {
  const [files, setFiles] = useState<PFile[]>([]);
  const [eots, setEots] = useState<Eot[]>([]);
  const load = async () => {
    const [{ data: f }, { data: e }] = await Promise.all([
      supabase
        .from('files')
        .select('id, original_name, mime, kind, created_at, programme, status_tag, review_flag, superseded_by, notes, note_by_name, note_at, is_current')
        .eq('linked_table', 'buildings')
        .eq('linked_id', buildingId)
        .eq('doc_type', 'programme')
        .is('deleted_at', null),
      supabase.from('items').select('id, number, subtype, eot_days_granted, status').eq('type', 'notice').contains('building_ids', [buildingId]).is('deleted_at', null),
    ]);
    setFiles(((f ?? []) as PFile[]).sort(byNewest));
    setEots(((e ?? []) as (Eot & { subtype: string | null })[]).filter((x) => /\bEOT\b/i.test(x.subtype ?? '') && x.status !== 'void' && x.status !== 'rejected'));
  };
  useEffect(() => void load(), [buildingId, version]); // eslint-disable-line react-hooks/exhaustive-deps
  useLive(['files'], () => void load(), { filter: `linked_id=eq.${buildingId}` });
  const granted = eots.reduce((s, x) => s + Number(x.eot_days_granted ?? 0), 0);
  return { files, eots, granted, reload: load };
}

export function ProgrammeSection({ building, canEdit, siteId, version, onReading, onBuildingChanged }: { building: Building; canEdit: boolean; siteId: string; version: number; onReading: () => void; onBuildingChanged: () => void }) {
  const toast = useToast();
  const viewer = useViewer();
  const { files, eots, granted, reload } = useProgrammes(building.id, version);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [edit, setEdit] = useState<PFile | null>(null);
  const [tagging, setTagging] = useState<string | null>(null);
  const [cmp, setCmp] = useState<string>('');
  const [all, setAll] = useState(false);
  const [over, setOver] = useState(false);
  const [openMs, setOpenMs] = useState<Record<string, boolean>>({});
  // collapsed by default (it is long); remembered on this browser
  const [open, setOpenRaw] = useState<boolean>(() => {
    try {
      return localStorage.getItem('pmc3.prog.open') === '1';
    } catch {
      return false;
    }
  });
  const setOpen = (v: boolean) => {
    setOpenRaw(v);
    try {
      localStorage.setItem('pmc3.prog.open', v ? '1' : '0');
    } catch {
      /* not remembered */
    }
  };

  const withDates = files.filter((f) => f.programme);
  const cur = withDates[0] ?? null;
  const prevFile = (cmp ? withDates.find((f) => f.id === cmp) : withDates[1]) ?? null;
  const rows = cur?.programme ? compare(cur.programme, prevFile?.programme ?? null) : [];
  const pcNow = cur?.programme?.pc_date ?? null;
  const chk = eotCheck(building.pc_date, granted, pcNow);
  const sincePrev = days(withDates[1]?.programme?.pc_date, pcNow);
  const hist = [...withDates].reverse().filter((f) => f.programme?.pc_date);
  const span = (() => {
    const ds = hist.map((f) => new Date(f.programme!.pc_date! + 'T00:00:00Z').getTime());
    const lo = Math.min(...ds, building.pc_date ? new Date(building.pc_date + 'T00:00:00Z').getTime() : Infinity);
    const hi = Math.max(...ds);
    return { lo, hi };
  })();

  async function upload(list: FileList | null) {
    const f = list?.[0];
    if (!f) return;
    setBusy('Uploading…');
    try {
      const r = await uploadFile(f, { siteId, linkedTable: 'buildings', linkedId: building.id });
      const id = (r.row as { id: string }).id;
      await write('files', 'update', { id, values: { doc_type: 'programme' }, summary: `Filed ${f.name} as a programme` });
      if (readable(f)) {
        setBusy('Starting Claude…');
        await api('building-read', { building_id: building.id, file_id: id });
        onReading();
      } else {
        toast({ tone: 'ok', text: `${f.name} saved. Claude reads PDF, Excel (.xlsx), CSV and photos — type the key dates for this one.` });
        await reload();
        setEdit({ id, original_name: f.name, created_at: new Date().toISOString(), programme: null, mime: f.type, kind: 'file' });
      }
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <section
      className={`card prog ${over ? 'over' : ''}`}
      aria-label="Programme"
      onDragOver={canEdit ? (e) => (e.preventDefault(), setOver(true)) : undefined}
      onDragLeave={canEdit ? (e) => !e.currentTarget.contains(e.relatedTarget as Node) && setOver(false) : undefined}
      onDrop={
        canEdit
          ? (e) => {
              e.preventDefault();
              setOver(false);
              void upload(e.dataTransfer.files);
            }
          : undefined
      }
    >
      <div className="card-head">
        <button type="button" className="prog-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          <svg className={`bchev ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
          <h2>Programme</h2>
        </button>
        {!open && pcNow ? (
          <span className={`prog-chip ${chk.beyond !== null && chk.beyond > 0 ? 'warn' : ''}`}>
            PC {fmtDate(pcNow)}
            {chk.beyond !== null && chk.beyond > 0 ? ` · ${chk.beyond} d past EOTs` : sincePrev ? ` · ${signed(sincePrev)}` : ''}
          </span>
        ) : null}
        <span className="muted small on-dark grow">
          {cur?.programme ? [cur.programme.rev, cur.programme.issued_by, cur.programme.data_date ? `data date ${fmtDate(cur.programme.data_date)}` : null, `${withDates.length} revision${withDates.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ') : 'No programme yet'}
        </span>
        {canEdit ? (
          <>
            <button type="button" className="btn btn-primary btn-sm" disabled={Boolean(busy)} onClick={() => input.current?.click()}>
              {busy ?? 'Upload programme — Claude reads it'}
            </button>
            <input ref={input} type="file" hidden accept=".pdf,.xlsx,.csv,.mpp,.xls,image/*" onChange={(e) => void upload(e.target.files)} aria-label="Programme file" />
          </>
        ) : null}
      </div>
      {over ? <div className="prog-dropnote">Drop the programme here — Claude reads it</div> : null}

      {!open ? null : !cur ? (
        <div className="card-body">
          {canEdit ? (
            <button type="button" className="rf-drop prog-drop" onClick={() => input.current?.click()} disabled={Boolean(busy)}>
              <b>{busy ?? 'Drop a programme here, or click to choose'}</b>
              <span className="muted small">PDF, Excel (.xlsx), CSV or photo — Claude reads it; you check before saving</span>
            </button>
          ) : null}
          <p className="muted small" style={{ margin: 0 }}>
            Upload each programme revision from PBS (PDF, Excel or CSV) — Claude reads the revision, data date, PC date and key milestones; you check them, then the page shows what moved since the last revision and how the PC date compares with the EOTs granted.
            {files.length ? ` ${files.length} programme file${files.length === 1 ? '' : 's'} without dates yet — use "Type dates" below.` : ''}
          </p>
        </div>
      ) : (
        <>
          <div className="prog-kpis">
            <div>
              <span className="k">Contract PC</span>
              <b>{fmtDate(building.pc_date)}</b>
              <span className="small muted">from the building / contract</span>
            </div>
            <div>
              <span className="k">EOT granted</span>
              <b>{granted ? `${granted} days` : '—'}</b>
              <span className="small muted">
                {eots.filter((x) => x.eot_days_granted).length} EOT{eots.filter((x) => x.eot_days_granted).length === 1 ? '' : 's'} determined{chk.eotDate && granted ? ` → ${fmtDate(chk.eotDate)}` : ''}
              </span>
            </div>
            <div className={chk.beyond !== null && chk.beyond > 0 ? 'warn' : ''}>
              <span className="k">Programme PC · {cur.programme?.rev ?? 'latest'}</span>
              <b>{fmtDate(pcNow)}</b>
              <span className="small">
                {chk.onContract !== null ? `${signed(chk.onContract)} on contract` : 'no contract PC'}
                {sincePrev !== null ? ` · ${signed(sincePrev)} since ${withDates[1]?.programme?.rev ?? 'last'}` : ''}
              </span>
            </div>
            <div>
              <span className="k">Revised PC (building)</span>
              <b>{fmtDate(building.revised_pc_date)}</b>
              <span className="small muted">
                {pcNow && pcNow !== building.revised_pc_date && canEdit ? (
                  <button
                    type="button"
                    className="linkbtn strong"
                    onClick={async () => {
                      try {
                        await write('buildings', 'update', { id: building.id, values: { revised_pc_date: pcNow, _expect_updated_at: building.updated_at }, summary: `Revised PC → ${pcNow} (from programme ${cur.programme?.rev ?? cur.original_name})` });
                        onBuildingChanged();
                      } catch (e) {
                        toast({ tone: 'error', text: errText(e) });
                      }
                    }}
                  >
                    Use {cur.programme?.rev ?? 'the programme'}'s date
                  </button>
                ) : pcNow && pcNow === building.revised_pc_date ? (
                  'matches the programme'
                ) : (
                  'not set'
                )}
              </span>
            </div>
            <div>
              <span className="k">PC date by revision</span>
              <span className="prog-bars" role="img" aria-label={`PC date by revision: ${hist.map((f) => `${f.programme?.rev ?? ''} ${fmtDate(f.programme?.pc_date)}`).join(', ')}`}>
                {hist.slice(-8).map((f) => {
                  const t = new Date(f.programme!.pc_date! + 'T00:00:00Z').getTime();
                  const h = span.hi > span.lo ? 8 + ((t - span.lo) / (span.hi - span.lo)) * 34 : 24;
                  return <i key={f.id} className={f.id === cur.id ? 'on' : ''} style={{ height: `${h}px` }} title={`${f.programme?.rev ?? f.original_name}: ${fmtDate(f.programme?.pc_date)}`} />;
                })}
              </span>
              <span className="small muted">{hist.length > 1 ? `${hist[0].programme?.rev ?? 'first'} → ${hist[hist.length - 1].programme?.rev ?? 'latest'}` : 'one revision so far'}</span>
            </div>
          </div>

          {chk.beyond !== null && chk.beyond > 0 ? (
            <div className="prog-warn" role="status">
              <span className="tagx">vs EOTs</span>
              <span className="grow">
                <b>
                  Programme PC is {chk.beyond} day{chk.beyond === 1 ? '' : 's'} past {granted ? `the EOT-adjusted date (${fmtDate(chk.eotDate)})` : `the contract PC (${fmtDate(building.pc_date)}) and no EOT is granted`}.
                </b>{' '}
                No granted EOT covers the extra time — raise it with PBS, or check for an EOT notice not yet logged.
              </span>
              <Link to={`/r/notices?b=${building.id}`} className="strong small">
                Notices (EOT) →
              </Link>
            </div>
          ) : null}

          <div className="prog-body">
            <div className="prog-ms">
              <div className="resp-row">
                <b className="k">Key milestones</b>
                <span className="muted small grow">
                  {cur.programme?.rev ?? 'Latest'} vs{' '}
                  {withDates.length > 1 ? (
                    <select aria-label="Compare with" value={prevFile?.id ?? ''} onChange={(e) => setCmp(e.target.value)}>
                      {withDates.slice(1).map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.programme?.rev ?? f.original_name}
                          {f.programme?.data_date ? ` · ${fmtDate(f.programme.data_date)}` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    'no earlier revision yet'
                  )}{' '}
                  · moved later first
                </span>
              </div>
              <div className="table-wrap">
                <table className="t prog-t">
                  <thead>
                    <tr>
                      <th>Milestone</th>
                      <th>{prevFile?.programme?.rev ?? 'Before'}</th>
                      <th>{cur.programme?.rev ?? 'Now'}</th>
                      <th className="right">Moved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(all ? rows : rows.slice(0, 10)).map((r) => (
                      <Fragment key={r.name}>
                        <tr className={`mv-${r.state}`}>
                          <td>
                            {r.items.length ? (
                              <button type="button" className="ms-toggle" aria-expanded={Boolean(openMs[r.name])} onClick={() => setOpenMs({ ...openMs, [r.name]: !openMs[r.name] })}>
                                <svg className={`bchev ${openMs[r.name] ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                                  <path d="M9 6l6 6-6 6" />
                                </svg>
                                {r.name}
                                <span className="ms-n">
                                  {r.items.length} activit{r.items.length === 1 ? 'y' : 'ies'}
                                  {r.items.filter((i) => i.state === 'later').length ? ` · ${r.items.filter((i) => i.state === 'later').length} later` : ''}
                                </span>
                              </button>
                            ) : (
                              r.name
                            )}
                          </td>
                          <td className="muted">{fmtDate(r.prev)}</td>
                          <td>
                            <b>{fmtDate(r.date)}</b>
                          </td>
                          <td className="right">
                            <span className={`mv mv-${r.state}`}>{r.state === 'done' ? '✓ done' : r.state === 'new' ? 'new' : r.state === 'gone' ? 'dropped' : r.state === 'same' ? '—' : signed(r.moved)}</span>
                          </td>
                        </tr>
                        {openMs[r.name]
                          ? r.items.map((i, k) => (
                              <tr key={`${r.name}-${k}`} className="ms-item">
                                <td>{i.name}</td>
                                <td className="muted">{fmtDate(i.prev)}</td>
                                <td>{fmtDate(i.date)}</td>
                                <td className="right">
                                  <span className={`mv mv-${i.state}`}>{i.state === 'done' ? '✓ done' : i.state === 'new' ? 'new' : i.state === 'same' ? '—' : signed(i.moved)}</span>
                                </td>
                              </tr>
                            ))
                          : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 10 ? (
                <button type="button" className="linkbtn small" onClick={() => setAll(!all)}>
                  {all ? 'Show fewer' : `Show all ${rows.length}`}
                </button>
              ) : null}
            </div>

            <div className="prog-revs">
              <b className="k">Revisions</b>
              {files.map((f, i) => (
                <div key={f.id} className={`prog-rev ${i === 0 ? '' : 'old'}`}>
                  <div className="prog-rev-top">
                    <span className={`ext ${fileTag(f.original_name, f.kind).cls}`}>{fileTag(f.original_name, f.kind).label}</span>
                    <button type="button" className="linkbtn grow" onClick={() => viewer({ id: f.id, name: f.original_name, mime: f.mime, kind: f.kind })}>
                      {f.programme?.rev ?? f.original_name}
                      {f.programme?.data_date ? ` · ${fmtDate(f.programme.data_date)}` : ''}
                    </button>
                    <FileStatus f={f} others={files} />
                  </div>
                  <span className="small muted">
                    {f.programme ? `PC ${fmtDate(f.programme.pc_date)} · ${f.programme.milestones.length} milestones` : 'no dates yet'}
                    {f.programme?.rev ? ` · ${f.original_name}` : ''}
                  </span>
                  <FileNote f={f} />
                  {canEdit ? (
                    <span className="cmt-actions">
                      <button type="button" className="linkbtn" onClick={() => setEdit(f)}>
                        {f.programme ? 'Edit dates' : 'Type dates'}
                      </button>
                      <button type="button" className="linkbtn" aria-label={`Tag and note for ${f.original_name}`} onClick={() => setTagging(tagging === f.id ? null : f.id)}>
                        Tag · note
                      </button>
                    </span>
                  ) : null}
                  {tagging === f.id ? (
                    <TagEditor
                      f={f}
                      others={files}
                      onCancel={() => setTagging(null)}
                      onDone={() => {
                        setTagging(null);
                        void reload();
                      }}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {open && !cur && files.length && canEdit ? (
        <div className="card-body prog-revs">
          {files.map((f) => (
            <div key={f.id} className="prog-rev">
              <div className="prog-rev-top">
                <span className={`ext ${fileTag(f.original_name, f.kind).cls}`}>{fileTag(f.original_name, f.kind).label}</span>
                <span className="grow">{f.original_name}</span>
                <button type="button" className="linkbtn strong" onClick={() => setEdit(f)}>
                  Type dates
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {edit ? (
        <Drawer open wide title={`${building.name} — programme dates`} onClose={() => setEdit(null)}>
          <ProgrammeEditor
            building={building}
            file={edit}
            initial={edit.programme}
            all={files}
            granted={granted}
            onCancel={() => setEdit(null)}
            onDone={() => {
              setEdit(null);
              void reload();
              onBuildingChanged();
            }}
          />
        </Drawer>
      ) : null}
    </section>
  );
}

/** Check Claude's read, or type the dates by hand. Saves the dates on the file, tags it, and can update Revised PC. */
export function ProgrammeEditor({
  building,
  file,
  initial,
  all,
  granted,
  aiCallId,
  claudeNotes,
  onDone,
  onCancel,
}: {
  building: Building;
  file: { id: string; original_name: string; notes?: string | null; status_tag?: string | null };
  initial: Programme | null;
  all: PFile[];
  granted: number;
  aiCallId?: string;
  claudeNotes?: string | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const toast = useToast();
  const start = cleanProgramme(initial);
  const [p, setP] = useState<Programme>(start);
  const [keep, setKeep] = useState<boolean[]>(() => start.milestones.map(() => true));
  const [note, setNote] = useState(file.notes ?? '');
  const [busy, setBusy] = useState(false);
  const others = all.filter((f) => f.id !== file.id && f.programme).sort(byNewest);
  const prev = others.find((f) => (f.programme?.data_date ?? '') <= (p.data_date ?? '9999')) ?? null;
  const newest = !others.some((f) => (f.programme?.data_date ?? '') > (p.data_date ?? ''));
  const [usePc, setUsePc] = useState(true);
  const [openIt, setOpenIt] = useState<Record<number, boolean>>({});
  const nItems = p.milestones.reduce((s, m) => s + (m.items?.length ?? 0), 0);
  const kept = useMemo<Programme>(() => ({ ...p, milestones: p.milestones.filter((_, i) => keep[i]) }), [p, keep]);
  const rows = compare(kept, prev?.programme ?? null);
  const moved = new Map(rows.map((r) => [r.name, r]));
  const chk = eotCheck(building.pc_date, granted, p.pc_date);
  const sincePrev = days(prev?.programme?.pc_date, p.pc_date);
  const unmatchedPrev = (prev?.programme?.milestones ?? []).filter((m) => !kept.milestones.some((k) => k.name.toLowerCase() === m.name.toLowerCase() || (k.was ?? '').toLowerCase() === m.name.toLowerCase()));
  const pcChange = newest && p.pc_date && p.pc_date !== building.revised_pc_date;

  const setM = (i: number, v: Partial<Milestone>) => setP({ ...p, milestones: p.milestones.map((m, j) => (j === i ? { ...m, ...v } : m)) });

  async function save() {
    const clean = cleanProgramme(kept);
    if (!clean.pc_date && !clean.milestones.length) return toast({ tone: 'error', text: 'Add at least the PC date or one milestone' });
    setBusy(true);
    const batch = crypto.randomUUID();
    const by = aiCallId ? ' (read by Claude, confirmed)' : '';
    try {
      const values: Record<string, unknown> = { doc_type: 'programme', programme: clean };
      if (newest) values.status_tag = 'latest';
      if (note.trim() !== (file.notes ?? '')) values.notes = note.trim() || null;
      await write('files', 'update', { id: file.id, batch_id: batch, values, summary: `Programme ${clean.rev ?? file.original_name}: ${clean.milestones.length} milestones, PC ${clean.pc_date ?? '—'}${by}` });
      if (newest)
        for (const o of all.filter((f) => f.id !== file.id && (!f.status_tag || f.status_tag === 'latest')))
          await write('files', 'update', { id: o.id, batch_id: batch, values: { status_tag: 'outdated' }, summary: `${o.programme?.rev ?? o.original_name} outdated by ${clean.rev ?? file.original_name}` });
      if (pcChange && usePc)
        await write('buildings', 'update', { id: building.id, batch_id: batch, values: { revised_pc_date: clean.pc_date }, summary: `Revised PC → ${clean.pc_date} (from programme ${clean.rev ?? file.original_name})${by}` });
      if (aiCallId) await api('building-read', { ai_call_id: aiCallId, confirm: true });
      toast({ tone: 'ok', text: `Programme ${clean.rev ?? ''} saved` });
      onDone();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="prog-edit">
      <p className="muted small" style={{ margin: 0 }}>
        {file.original_name}
        {aiCallId ? ' · read by Claude — check each date; nothing is saved until you press Save' : ' · type the dates as shown on the programme'}
      </p>
      <div className="prog-edit-head">
        <label className="field">
          <span className="field-label">Revision</span>
          <input value={p.rev ?? ''} placeholder="e.g. Rev 07" onChange={(e) => setP({ ...p, rev: e.target.value || null })} />
        </label>
        <label className="field">
          <span className="field-label">Data date</span>
          <input type="date" value={p.data_date ?? ''} onChange={(e) => setP({ ...p, data_date: e.target.value || null })} />
        </label>
        <label className="field">
          <span className="field-label">Issued by</span>
          <input value={p.issued_by ?? ''} placeholder="e.g. PBS" onChange={(e) => setP({ ...p, issued_by: e.target.value || null })} />
        </label>
        <label className="field">
          <span className="field-label">PC date (this building)</span>
          <input type="date" value={p.pc_date ?? ''} onChange={(e) => setP({ ...p, pc_date: e.target.value || null })} />
        </label>
      </div>

      <div className="resp-row">
        <b className="grow">
          Milestones · {kept.milestones.length}
          {nItems ? <span className="muted small"> · {nItems} activities under them (click a milestone to show)</span> : null}
        </b>
        {nItems ? (
          <button type="button" className="linkbtn small" onClick={() => setOpenIt(Object.keys(openIt).some((k) => openIt[Number(k)]) ? {} : Object.fromEntries(p.milestones.map((_, i) => [i, true])))}>
            {Object.keys(openIt).some((k) => openIt[Number(k)]) ? 'Collapse all' : 'Expand all'}
          </button>
        ) : null}
        <span className="muted small">{prev ? `compared with ${prev.programme?.rev ?? prev.original_name}` : 'first revision — nothing to compare yet'}</span>
      </div>
      <div className="table-wrap">
        <table className="t prog-t">
          <thead>
            <tr>
              <th>Keep</th>
              <th>Milestone</th>
              <th>Date</th>
              <th>Done</th>
              <th className="right">Moved</th>
              {aiCallId ? <th>Where</th> : null}
            </tr>
          </thead>
          <tbody>
            {p.milestones.map((m, i) => {
              const r = moved.get(m.name);
              const its = m.items ?? [];
              return (
                <Fragment key={i}>
                <tr className={r?.state === 'new' ? 'mv-new' : ''}>
                  <td>
                    <input type="checkbox" aria-label={`Keep ${m.name || 'milestone'}`} checked={keep[i] ?? true} onChange={(e) => setKeep(keep.map((k, j) => (j === i ? e.target.checked : k)))} />
                  </td>
                  <td>
                    <input aria-label="Milestone name" value={m.name} onChange={(e) => setM(i, { name: e.target.value })} />
                    {its.length ? (
                      <button type="button" className="ms-toggle small" aria-expanded={Boolean(openIt[i])} onClick={() => setOpenIt({ ...openIt, [i]: !openIt[i] })}>
                        <svg className={`bchev ${openIt[i] ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                        {its.length} activit{its.length === 1 ? 'y' : 'ies'}
                      </button>
                    ) : null}
                    {r?.state === 'new' && keep[i] && unmatchedPrev.length ? (
                      <select aria-label={`Was ${m.name} called something else before?`} className="small" value={m.was ?? ''} onChange={(e) => setM(i, { was: e.target.value || null })}>
                        <option value="">new milestone</option>
                        {unmatchedPrev.map((u) => (
                          <option key={u.name} value={u.name}>
                            same as “{u.name}”
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </td>
                  <td>
                    <input type="date" aria-label={`Date for ${m.name}`} value={m.date ?? ''} onChange={(e) => setM(i, { date: e.target.value || null })} />
                  </td>
                  <td>
                    <input type="checkbox" aria-label={`${m.name} done`} checked={Boolean(m.actual)} onChange={(e) => setM(i, { actual: e.target.checked })} />
                  </td>
                  <td className="right">{keep[i] && r ? <span className={`mv mv-${r.state}`}>{r.state === 'done' ? 'done' : r.state === 'new' ? 'new' : r.state === 'same' ? '—' : signed(r.moved)}</span> : null}</td>
                  {aiCallId ? <td className="small where">{m.where ?? ''}</td> : null}
                </tr>
                {openIt[i]
                  ? its.map((it, k) => {
                      const im = r?.items[k];
                      return (
                        <tr key={`${i}-${k}`} className="ms-item">
                          <td />
                          <td>
                            {it.name}
                            {it.start ? <span className="muted small"> · from {fmtDate(it.start)}</span> : null}
                          </td>
                          <td>{fmtDate(it.date)}</td>
                          <td>{it.actual ? '✓' : ''}</td>
                          <td className="right">{im && keep[i] ? <span className={`mv mv-${im.state}`}>{im.state === 'done' ? 'done' : im.state === 'new' ? 'new' : im.state === 'same' ? '—' : signed(im.moved)}</span> : null}</td>
                          {aiCallId ? <td className="small where">{it.where ?? ''}</td> : null}
                        </tr>
                      );
                    })
                  : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <button type="button" className="linkbtn strong" onClick={() => (setP({ ...p, milestones: [...p.milestones, { name: '', date: null, actual: false }] }), setKeep([...keep, true]))}>
        + Milestone
      </button>

      {p.pc_date ? (
        <div className={`prog-checks ${chk.beyond !== null && chk.beyond > 0 ? 'warn' : ''}`}>
          <b>Checks</b>
          <span>
            PC {fmtDate(p.pc_date)}
            {sincePrev !== null ? ` · ${signed(sincePrev)} since ${prev?.programme?.rev ?? 'the last revision'}` : ''}
            {chk.onContract !== null ? ` · ${signed(chk.onContract)} on the contract PC (${fmtDate(building.pc_date)})` : ' · no contract PC on the building'}
            {chk.beyond !== null ? (chk.beyond > 0 ? ` · granted EOTs (${granted} d) leave ${chk.beyond} days not covered` : ` · within the EOTs granted (${granted} d)`) : ''}
          </span>
          {pcChange ? (
            <label className="check">
              <input type="checkbox" checked={usePc} onChange={(e) => setUsePc(e.target.checked)} /> Update the building's Revised PC to {fmtDate(p.pc_date)} (now {fmtDate(building.revised_pc_date)})
            </label>
          ) : null}
        </div>
      ) : null}
      {claudeNotes ? <div className="banner banner-info">Claude's notes: {claudeNotes}</div> : null}

      <label className="field">
        <span className="field-label">Note on this revision {newest ? '· it becomes Latest; the one before becomes Outdated' : ''}</span>
        <textarea rows={2} value={note} placeholder="e.g. PBS cite barge delays — ask for the EOT notice" onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="resp-row">
        <span className="grow" />
        <Button onClick={onCancel}>Cancel</Button>
        <Button kind="primary" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : `Save ${p.rev ?? 'programme'} · ${kept.milestones.length} milestones${pcChange && usePc ? ' · Revised PC' : ''}`}
        </Button>
      </div>
    </div>
  );
}
